import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Boxes, PlayCircle, PauseCircle, CheckCircle2, ArrowUpCircle, RotateCcw, Trash2, ShieldCheck, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };

const STATE_COLOR = {
  enabled: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  disabled: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  suspended: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  configured: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  installing: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  configuration_required: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  dependency_unavailable: "bg-red-500/15 text-red-400 border-red-500/30",
  migration_required: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  retired: "bg-zinc-700/30 text-zinc-500 border-zinc-600/30",
};

export function ModuleControlPlane() {
  const [catalog, setCatalog] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [installs, setInstalls] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState("");

  const loadCatalog = useCallback(async () => {
    try {
      const [c, t] = await Promise.all([
        axios.get(`${API}/platform/modules/catalog`, cfg),
        axios.get(`${API}/platform/tenants`, cfg),
      ]);
      setCatalog(c.data.modules || []);
      const list = (t.data.tenants || t.data || []).map((x) => ({ org_id: x.org_id || x.id, name: x.name || x.org_id || x.id }));
      setTenants(list);
      if (!tenantId && list.length) setTenantId(list[0].org_id);
    } catch (e) {
      toast.error("Failed to load module catalog");
    }
  }, [tenantId]);

  const loadInstalls = useCallback(async (tid) => {
    if (!tid) return;
    try {
      const r = await axios.get(`${API}/platform/modules/tenants/${tid}/installations`, cfg);
      setInstalls(r.data.installations || []);
    } catch { setInstalls([]); }
  }, []);

  const loadProposals = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/platform/modules/migration/proposals`, cfg);
      setProposals(r.data.proposals || []);
    } catch { setProposals([]); }
  }, []);

  useEffect(() => { loadCatalog(); loadProposals(); }, [loadCatalog, loadProposals]);
  useEffect(() => { loadInstalls(tenantId); setAudit(null); }, [tenantId, loadInstalls]);

  const findInstall = (mid) => installs.find((i) => i.module_id === mid);

  const act = async (label, fn, mid) => {
    setBusy(`${mid}:${label}`);
    try {
      await fn();
      toast.success(`${label} ok`);
      await loadInstalls(tenantId);
    } catch (e) {
      toast.error(`${label} failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(""); }
  };

  const install = (m) => act("install", () =>
    axios.post(`${API}/platform/modules/tenants/${tenantId}/install`, { module_id: m.module_id, version: m.version }, cfg), m.module_id);
  const configure = (i) => act("configure", () =>
    axios.post(`${API}/platform/modules/tenants/${tenantId}/${i.module_id}/configure`, { configuration: { configured_at: Date.now() }, expected_rev: i.rev }, cfg), i.module_id);
  const validate = (i) => act("validate", () =>
    axios.post(`${API}/platform/modules/tenants/${tenantId}/${i.module_id}/validate`, {}, cfg), i.module_id);
  const activate = (i) => act("activate", () =>
    axios.post(`${API}/platform/modules/tenants/${tenantId}/${i.module_id}/activate`, { expected_rev: i.rev }, cfg), i.module_id);
  const suspend = (i) => act("suspend", () =>
    axios.post(`${API}/platform/modules/tenants/${tenantId}/${i.module_id}/suspend`, { expected_rev: i.rev, reason: "admin" }, cfg), i.module_id);
  const resume = (i) => act("resume", () =>
    axios.post(`${API}/platform/modules/tenants/${tenantId}/${i.module_id}/resume`, { expected_rev: i.rev }, cfg), i.module_id);
  const retire = (i) => act("retire", () =>
    axios.post(`${API}/platform/modules/tenants/${tenantId}/${i.module_id}/retire`, { expected_rev: i.rev }, cfg), i.module_id);

  const showAudit = async (mid) => {
    const r = await axios.get(`${API}/platform/modules/tenants/${tenantId}/${mid}/audit`, cfg);
    setAudit({ module_id: mid, transitions: r.data.transitions || [] });
  };

  const generateMigration = async () => {
    setBusy("migrate");
    try {
      const r = await axios.post(`${API}/platform/modules/migration/generate`, {}, cfg);
      toast.success(`Migration report: ${r.data.summary?.proposals} proposals, ${r.data.summary?.quarantined} quarantined`);
      await loadProposals();
    } catch (e) { toast.error("Migration generate failed"); }
    finally { setBusy(""); }
  };
  const approveProposal = async (p) => {
    try {
      await axios.post(`${API}/platform/modules/migration/proposals/${p.tenant_id}/${p.module_id}/approve`, {}, cfg);
      toast.success(`Approved ${p.module_id} for ${p.tenant_id}`);
      await loadProposals(); await loadInstalls(tenantId);
    } catch (e) { toast.error(`Approve failed: ${e?.response?.data?.detail?.error || e?.response?.data?.error || e.message}`); }
  };
  const mapOrphan = async (p) => {
    const mapped = window.prompt(`Map orphan reference "${p.tenant_id}" to a valid canonical tenant id:`);
    if (!mapped) return;
    try {
      await axios.post(`${API}/platform/modules/migration/proposals/${p.tenant_id}/${p.module_id}/map`, { mapped_tenant_id: mapped }, cfg);
      toast.success(`Mapped ${p.tenant_id} → ${mapped}`);
      await loadProposals();
    } catch (e) { toast.error(`Map failed: ${e?.response?.data?.detail?.error || e.message}`); }
  };

  return (
    <div className="space-y-6" data-testid="module-control-plane">
      <div className="flex items-center gap-3">
        <Boxes className="w-6 h-6 text-emerald-400" />
        <div>
          <h2 className="text-lg font-semibold text-white">Module Control Plane</h2>
          <p className="text-sm text-zinc-400">Versioned Module Registry and per-tenant installations. Fail-closed entitlements.</p>
        </div>
        <Button variant="outline" size="sm" className="ml-auto" onClick={() => { loadCatalog(); loadInstalls(tenantId); }} data-testid="mcp-refresh">
          <RefreshCw className="w-4 h-4 mr-1" />Refresh
        </Button>
      </div>

      {/* Tenant selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-zinc-400">Tenant</span>
        <select
          className="bg-zinc-900 border border-zinc-700 rounded px-3 py-1.5 text-sm text-white"
          value={tenantId} onChange={(e) => setTenantId(e.target.value)} data-testid="mcp-tenant-select">
          {tenants.map((t) => <option key={t.org_id} value={t.org_id}>{t.name} ({t.org_id})</option>)}
        </select>
      </div>

      {/* Catalog + install */}
      <Card className="p-4 bg-zinc-900/60 border-zinc-800" data-testid="mcp-catalog">
        <h3 className="text-sm font-semibold text-white mb-3">Module Catalog ({catalog.length})</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {catalog.map((m) => {
            const inst = findInstall(m.module_id);
            return (
              <div key={`${m.module_id}:${m.version}`} className="border border-zinc-800 rounded-lg p-3 bg-zinc-950/50" data-testid={`mcp-module-${m.module_id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-white text-sm">{m.identity?.display_name || m.module_id}</span>
                  <Badge variant="outline" className="text-xs">v{m.version}</Badge>
                </div>
                <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{m.identity?.description}</p>
                <div className="mt-2 flex items-center gap-2">
                  {inst
                    ? <Badge className={`text-xs border ${STATE_COLOR[inst.status] || ""}`} data-testid={`mcp-status-${m.module_id}`}>{inst.status}</Badge>
                    : <Button size="sm" variant="outline" disabled={!tenantId || busy} onClick={() => install(m)} data-testid={`mcp-install-${m.module_id}`}>Install</Button>}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Installations lifecycle */}
      <Card className="p-4 bg-zinc-900/60 border-zinc-800" data-testid="mcp-installations">
        <h3 className="text-sm font-semibold text-white mb-3">Installations — {tenantId || "(no tenant)"}</h3>
        {installs.length === 0 && <p className="text-sm text-zinc-500">No installed Modules for this tenant.</p>}
        <div className="space-y-2">
          {installs.map((i) => (
            <div key={i.module_id} className="flex flex-wrap items-center gap-2 border border-zinc-800 rounded-lg p-3 bg-zinc-950/50" data-testid={`mcp-install-row-${i.module_id}`}>
              <span className="font-medium text-white text-sm w-40">{i.module_id}</span>
              <Badge className={`text-xs border ${STATE_COLOR[i.status] || ""}`}>{i.status}</Badge>
              <span className="text-xs text-zinc-500">v{i.module_version} · rev {i.rev} · health {i.health}</span>
              <div className="flex items-center gap-1 ml-auto">
                {(i.status === "installing" || i.status === "configured") &&
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => configure(i)} data-testid={`mcp-configure-${i.module_id}`}>Configure</Button>}
                {i.status === "configured" &&
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => validate(i)} data-testid={`mcp-validate-${i.module_id}`}><ShieldCheck className="w-4 h-4 mr-1" />Validate</Button>}
                {i.status === "configured" &&
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => activate(i)} data-testid={`mcp-activate-${i.module_id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Activate</Button>}
                {i.status === "active" &&
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => suspend(i)} data-testid={`mcp-suspend-${i.module_id}`}><PauseCircle className="w-4 h-4 mr-1" />Suspend</Button>}
                {i.status === "suspended" &&
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => resume(i)} data-testid={`mcp-resume-${i.module_id}`}><PlayCircle className="w-4 h-4 mr-1" />Resume</Button>}
                {i.status !== "retired" &&
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => retire(i)} data-testid={`mcp-retire-${i.module_id}`}><Trash2 className="w-4 h-4 mr-1" />Retire</Button>}
                <Button size="sm" variant="ghost" onClick={() => showAudit(i.module_id)} data-testid={`mcp-audit-${i.module_id}`}>Audit</Button>
              </div>
            </div>
          ))}
        </div>
        {audit && (
          <div className="mt-3 border-t border-zinc-800 pt-3" data-testid="mcp-audit-panel">
            <p className="text-xs font-semibold text-zinc-300 mb-1">Audit — {audit.module_id}</p>
            <div className="space-y-1 max-h-48 overflow-auto text-xs font-mono text-zinc-400">
              {audit.transitions.map((t) => (
                <div key={t.seq}>#{t.seq} {t.action}: {t.from_state} → {t.to_state} · {t.actor} · {t.at}</div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Legacy migration */}
      <Card className="p-4 bg-zinc-900/60 border-zinc-800" data-testid="mcp-migration">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white">Legacy Entitlement Migration</h3>
          <Button size="sm" variant="outline" disabled={busy === "migrate"} onClick={generateMigration} data-testid="mcp-generate-migration">
            <ArrowUpCircle className="w-4 h-4 mr-1" />Generate Proposals
          </Button>
        </div>
        {proposals.length === 0 && <p className="text-sm text-zinc-500">No proposals. Generate from current entitlements.</p>}
        <div className="space-y-1">
          {proposals.map((p) => (
            <div key={`${p.tenant_id}:${p.module_id}`} className="flex items-center gap-2 text-xs border border-zinc-800 rounded px-3 py-2 bg-zinc-950/50" data-testid={`mcp-proposal-${p.tenant_id}-${p.module_id}`}>
              <span className="text-white w-40 truncate">{p.tenant_id}</span>
              <span className="text-zinc-300 w-32">{p.module_id}</span>
              <Badge variant="outline" className="text-xs">{p.source}</Badge>
              {p.status === "orphaned" && <Badge className="text-xs bg-red-500/15 text-red-400 border border-red-500/30" data-testid={`mcp-orphan-${p.tenant_id}-${p.module_id}`}>orphaned_tenant_reference</Badge>}
              {p.quarantined && p.status !== "orphaned" && <Badge className="text-xs bg-amber-500/15 text-amber-400 border border-amber-500/30">quarantined · review</Badge>}
              {p.mapped_tenant_id && <Badge variant="outline" className="text-xs">→ {p.mapped_tenant_id}</Badge>}
              <Badge variant="outline" className="text-xs">{p.status}</Badge>
              <div className="ml-auto flex items-center gap-1">
                {p.status === "orphaned" && !p.mapped_tenant_id &&
                  <Button size="sm" variant="ghost" onClick={() => mapOrphan(p)} data-testid={`mcp-map-${p.tenant_id}-${p.module_id}`}>Map to Tenant</Button>}
                {(p.status === "proposed" || (p.status === "orphaned" && p.mapped_tenant_id)) &&
                  <Button size="sm" variant="ghost" onClick={() => approveProposal(p)} data-testid={`mcp-approve-${p.tenant_id}-${p.module_id}`}>
                    <RotateCcw className="w-4 h-4 mr-1" />Approve & Activate
                  </Button>}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default ModuleControlPlane;
